import { Cpu, Eye, EyeOff, Loader2, Plus, Settings2, Star, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "@/components/shared";
import { useApp } from "@/context/AppContext";
import { useUpdateTierDefault } from "@/http/mutations/model/useUpdateTierDefault";
import { getApiErrorMessage } from "@/lib/api";
import { formatDateOnly } from "@/lib/formatters";
import { modelService } from "@/lib/services";
import type { AIModel, ModelReferences, ModelTierDefault } from "@/types";
import { AddModelModal } from "./AddModelModal";
import { ConfigureModelModal } from "./ConfigureModelModal";
import { describeUsage, type ReassignAction, ReassignModelModal } from "./ReassignModelModal";

// Display order and labels for tiers in the admin panel
const TIER_CONFIG: { tier: string; icon: string }[] = [
  { tier: "fast", icon: "zap" },
  { tier: "thorough", icon: "brain" },
  { tier: "router", icon: "shuffle" },
  { tier: "embedding", icon: "database" },
  { tier: "image", icon: "image" },
  { tier: "transcription", icon: "mic" },
  { tier: "retrieval", icon: "search" },
  { tier: "title", icon: "type" },
  { tier: "agent_routing", icon: "route" },
];

interface ReassignDialogState {
  model: AIModel;
  action: ReassignAction;
  references: ModelReferences;
  resolve: (confirmed: boolean) => void;
}

type SortOption = "name" | "cost" | "status";

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: "name", label: "Name (A–Z)" },
  { value: "cost", label: "Cost tier" },
  { value: "status", label: "Enabled first" },
];

const COST_TIER_RANK: Record<string, number> = { $: 1, $$: 2, $$$: 3 };

const byName = (a: AIModel, b: AIModel) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" });

const isModelEnabled = (model: AIModel) => Boolean(model.is_enabled || model.isEnabled);

function sortModels(models: AIModel[], sortBy: SortOption): AIModel[] {
  const sorted = [...models];
  if (sortBy === "cost") {
    const rank = (m: AIModel) => COST_TIER_RANK[m.costTier ?? ""] ?? Number.MAX_SAFE_INTEGER;
    return sorted.sort((a, b) => rank(a) - rank(b) || byName(a, b));
  }
  if (sortBy === "status") {
    return sorted.sort((a, b) => Number(isModelEnabled(b)) - Number(isModelEnabled(a)) || byName(a, b));
  }
  return sorted.sort(byName);
}

export function AdminModels() {
  const { refreshModels } = useApp();
  const { showToast } = useToast();
  const [models, setModels] = useState<AIModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedModel, setSelectedModel] = useState<AIModel | null>(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [reassignDialog, setReassignDialog] = useState<ReassignDialogState | null>(null);
  const reassignPending = useRef(false);
  const [sortBy, setSortBy] = useState<SortOption>("name");

  // Tier defaults state
  const [tierDefaults, setTierDefaults] = useState<ModelTierDefault[]>([]);
  const [savingTier, setSavingTier] = useState<string | null>(null);
  const [settingDefaultFor, setSettingDefaultFor] = useState<string | null>(null);
  const { mutateAsync: updateTierDefault } = useUpdateTierDefault();

  useEffect(() => {
    loadModels();
    loadTierDefaults();
  }, []);

  const loadModels = async () => {
    try {
      setLoading(true);
      const response = await modelService.adminList();
      setModels(response.data || []);
    } catch (err) {
      console.error("Failed to load models:", err);
      showToast("Failed to load models", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadTierDefaults = async () => {
    try {
      const response = await modelService.listAllTierDefaults();
      if (response.data?.defaults) {
        setTierDefaults(response.data.defaults);
      }
    } catch (err) {
      console.error("Failed to load tier defaults:", err);
    }
  };

  const saveTierDefault = async (tier: string, modelId: string) => {
    await updateTierDefault({ tier, modelId: modelId || null });
    await loadTierDefaults();
    await refreshModels(); // Refresh AppContext so dropdowns pick up changes
  };

  const handleTierModelChange = async (tier: string, value: string) => {
    try {
      setSavingTier(tier);
      await saveTierDefault(tier, value);
      showToast("Model default updated", "success");
    } catch (err) {
      console.error("Failed to update tier default:", err);
      showToast("Failed to update model default", "error");
    } finally {
      setSavingTier(null);
    }
  };

  const confirmReassign = async (model: AIModel, action: ReassignAction): Promise<boolean> => {
    // A second dialog would replace the first one's resolver and leave its caller waiting.
    if (reassignPending.current) return false;
    reassignPending.current = true;
    try {
      const { data: references } = await modelService.getReferences(model.id);
      const inUse = references.affectedAgents.length > 0 || references.chatCount > 0;
      if (!inUse && action !== "delete") return true;
      return await new Promise<boolean>((resolve) => setReassignDialog({ model, action, references, resolve }));
    } finally {
      reassignPending.current = false;
    }
  };

  const settleReassign = (confirmed: boolean) => {
    reassignDialog?.resolve(confirmed);
    setReassignDialog(null);
  };

  const handleSetAsDefault = async (model: AIModel, tier: string) => {
    if (!tier) return;
    try {
      setSettingDefaultFor(model.id);
      await saveTierDefault(tier, model.id);
      showToast(`${model.name} is now assigned to ${tierLabel(tier)}`, "success");
    } catch (err) {
      console.error("Failed to set model as default:", err);
      showToast("Failed to update model default", "error");
    } finally {
      setSettingDefaultFor(null);
    }
  };

  const handleToggleEnabled = async (model: AIModel) => {
    if (togglingId) return;

    const newState = !(model.is_enabled || model.isEnabled);

    try {
      if (!newState && !(await confirmReassign(model, "disable"))) return;
      setTogglingId(model.id);
      await modelService.update(model.id, {
        is_enabled: newState,
      });
      await loadModels();
      showToast(`Model ${newState ? "enabled" : "disabled"} successfully`, "success");
    } catch (err) {
      console.error("Failed to toggle model:", err);
      showToast(getApiErrorMessage(err, "Failed to update model"), "error");
    } finally {
      setTogglingId(null);
    }
  };

  const handleConfigure = (model: AIModel) => {
    setSelectedModel(model);
    setShowConfigModal(true);
  };

  const handleAddModel = () => {
    setShowAddModal(true);
  };

  const handleDeleteModel = async (model: AIModel) => {
    try {
      if (!(await confirmReassign(model, "delete"))) return;
      setDeletingId(model.id);
      await modelService.delete(model.id);
      await loadModels();
      await refreshModels();
      showToast(`Model "${model.name}" deleted`, "success");
    } catch (err) {
      console.error("Failed to delete model:", err);
      showToast(getApiErrorMessage(err, "Failed to delete model"), "error");
    } finally {
      setDeletingId(null);
    }
  };

  const handleReassign = async (model: AIModel) => {
    try {
      if (await confirmReassign(model, "move")) await loadModels();
    } catch (err) {
      console.error("Failed to load model usage:", err);
      showToast(getApiErrorMessage(err, "Failed to load model usage"), "error");
    }
  };

  const formatProvider = (provider: string) => {
    if (provider === "azure_openai") return "Azure OpenAI";
    if (provider === "azure_foundry") return "Azure AI Foundry";
    if (provider === "anthropic_foundry") return "Azure AI Foundry (Claude)";
    return provider;
  };

  const costTierColor = (tier: string) => {
    if (tier === "$") return "text-emerald-400";
    if (tier === "$$") return "text-amber-400";
    return "text-rose-400";
  };

  const usageNote = ({ agentCount, chatCount }: AIModel) =>
    agentCount != null && `Used by ${describeUsage(agentCount, chatCount ?? 0)}.`;

  const deprecationNote = (model: AIModel) => {
    const { sunsetDate } = model;
    const replacement = models.find((m) => m.id === model.replacementModelId);
    return [
      replacement?.isEnabled && !replacement.isDeprecated && `Use ${replacement.name} instead.`,
      sunsetDate && `Sunset ${formatDateOnly(sunsetDate)}.`,
      usageNote(model),
    ]
      .filter(Boolean)
      .join(" ");
  };

  // Get the tier default for a given tier
  const getTierDefault = (tier: string): ModelTierDefault | undefined => tierDefaults.find((td) => td.tier === tier);

  const tierLabel = (tier: string) => getTierDefault(tier)?.label || tier.charAt(0).toUpperCase() + tier.slice(1);

  const sortedModels = useMemo(() => sortModels(models, sortBy), [models, sortBy]);

  const defaultTiersForModel = (model: AIModel): ModelTierDefault[] =>
    tierDefaults.filter((td) => td.modelId === model.id);

  const assignableTiersForModel = (model: AIModel): string[] => {
    if (!isModelEnabled(model)) return [];
    return TIER_CONFIG.map(({ tier }) => tier).filter(
      (tier) =>
        getTierDefault(tier) &&
        getTierDefault(tier)?.modelId !== model.id &&
        getModelsForTier(tier).some((m) => m.id === model.id),
    );
  };

  // Get models appropriate for a given tier
  const getModelsForTier = (tier: string): AIModel[] => {
    switch (tier) {
      case "fast":
      case "thorough":
      case "router":
      case "title":
      case "agent_routing":
        return models.filter((m) => m.modelType === "chat");
      case "retrieval":
        // Vector-store file_search runs on an Azure OpenAI Responses chat model.
        return models.filter(
          (m) =>
            m.modelType === "chat" && m.apiProtocol === "openai_responses" && m.hosting?.startsWith("azure_openai"),
        );
      case "embedding":
        return models.filter((m) => m.modelType === "embedding");
      case "image":
        return models.filter((m) => m.modelType === "image");
      case "transcription":
        return models.filter((m) => m.modelType === "audio");
      default:
        return models;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* ── Model Tier Defaults ─────────────────────────────── */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Settings2 className="h-5 w-5 text-brand-primary" />
          <h2 className="text-lg font-semibold text-content-primary">Model Defaults</h2>
        </div>
        <p className="text-sm text-content-tertiary">
          Assign which model fills each role. User-facing tiers (Fast, Thorough, Choose for me) appear in model
          dropdowns throughout the app. When you swap to a newer model here, all users and agents using the default
          automatically benefit.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {TIER_CONFIG.map(({ tier }) => {
            const td = getTierDefault(tier);
            const tierModels = getModelsForTier(tier);
            const isSaving = savingTier === tier;

            return (
              <div key={tier} className="bg-surface-primary border border-border-primary rounded-xl p-4">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-medium text-content-primary">
                    {td?.label || tier.charAt(0).toUpperCase() + tier.slice(1)}
                  </h3>
                  {td?.userFacing && (
                    <span className="px-2 py-0.5 bg-brand-primary/20 text-brand-primary rounded-sm text-xs font-medium">
                      user-facing
                    </span>
                  )}
                </div>
                <p className="text-xs text-content-quaternary mb-3">
                  {td?.description || `Default model for ${tier} tasks`}
                </p>
                <div className="relative">
                  <select
                    value={td?.modelId || ""}
                    onChange={(e) => handleTierModelChange(tier, e.target.value)}
                    disabled={isSaving}
                    className="w-full appearance-none bg-surface-secondary text-content-primary pl-3 pr-8 py-2 rounded-lg border border-border-secondary focus:outline-hidden focus:ring-2 focus:ring-brand-primary text-sm disabled:opacity-50"
                  >
                    <option value="">Not configured</option>
                    {sortModels(tierModels, "name").map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                        {m.isDeprecated ? " (deprecated)" : ""}
                      </option>
                    ))}
                  </select>
                  {isSaving && (
                    <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary animate-spin" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Model Cards ─────────────────────────────────────── */}
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-content-tertiary">Configure AI models available to your organization</p>
            <p className="text-xs text-content-quaternary">
              List order doesn&apos;t affect which model is the default. Defaults are set in Model Defaults above or
              with &quot;Set as default&quot; on a model.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="model-sort" className="text-sm text-content-tertiary">
              Sort by
            </label>
            <select
              id="model-sort"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="appearance-none bg-surface-secondary text-content-primary pl-3 pr-8 py-2 rounded-lg border border-border-secondary focus:outline-hidden focus:ring-2 focus:ring-brand-primary text-sm"
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              onClick={handleAddModel}
              className="flex items-center gap-2 px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              <Plus className="h-4 w-4" />
              Add Model
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sortedModels.map((model) => {
            const isEnabled = isModelEnabled(model);
            const isToggling = togglingId === model.id;
            const inUse = (model.agentCount ?? 0) + (model.chatCount ?? 0) > 0;
            const note = model.isDeprecated ? deprecationNote(model) : inUse && usageNote(model);
            const defaultTiers = defaultTiersForModel(model);
            const assignableTiers = assignableTiersForModel(model);
            const isSettingDefault = settingDefaultFor === model.id;

            return (
              <div
                key={model.id}
                className="bg-surface-primary border border-border-primary rounded-xl p-5 hover:border-border-secondary transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-linear-to-br from-brand-primary/20 to-brand-secondary/20 flex items-center justify-center">
                      <Cpu className="h-5 w-5 text-brand-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-content-primary">{model.name}</h3>
                        {model.costTier && (
                          <span
                            className={`text-xs font-semibold tabular-nums ${costTierColor(model.costTier)}`}
                            title={`Cost tier: ${model.costTier}`}
                          >
                            {model.costTier}
                          </span>
                        )}
                        {model.isDeprecated && (
                          <span className="px-2 py-0.5 bg-amber-500/20 text-amber-800 dark:text-amber-200 rounded-sm text-xs font-medium">
                            Deprecated
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-content-tertiary">{formatProvider(model.provider)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleToggleEnabled(model)}
                    disabled={isToggling}
                    className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${
                      isEnabled
                        ? "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                        : "bg-surface-tertiary/20 text-content-tertiary hover:bg-interactive-active"
                    } ${isToggling ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {(() => {
                      if (isToggling) return <Loader2 className="w-3 h-3 animate-spin" />;
                      if (isEnabled)
                        return (
                          <>
                            <Eye className="w-3 h-3" />
                            Enabled
                          </>
                        );
                      return (
                        <>
                          <EyeOff className="w-3 h-3" />
                          Disabled
                        </>
                      );
                    })()}
                  </button>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-content-quaternary">Context Window</p>
                    <p className="text-lg font-semibold text-content-primary">
                      {(model.contextWindow || 0).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-content-quaternary">Max Output</p>
                    <p className="text-lg font-semibold text-content-primary">
                      {(model.maxOutputTokens || model.maxTokens || 0).toLocaleString()}
                    </p>
                  </div>
                </div>

                {defaultTiers.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {defaultTiers.map((td) => (
                      <span
                        key={td.tier}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand-primary/20 text-brand-primary rounded-sm text-xs font-medium"
                        title={`Default model for ${td.label}`}
                      >
                        <Star className="w-3 h-3" />
                        {td.label}
                      </span>
                    ))}
                  </div>
                )}

                {model.description && (
                  <p className="mt-3 text-xs text-content-tertiary leading-relaxed">{model.description}</p>
                )}
                {note && <p className="mt-2 text-xs text-amber-800 dark:text-amber-200">{note}</p>}
                {inUse && (
                  <button
                    onClick={() => handleReassign(model)}
                    className="mt-1 text-xs font-medium text-brand-primary hover:underline"
                  >
                    Move to another model…
                  </button>
                )}

                {/* Behavioral flags */}
                <div className="mt-3 flex flex-wrap gap-1">
                  {model.modelType && model.modelType !== "chat" && (
                    <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-sm text-xs font-medium">
                      {model.modelType}
                    </span>
                  )}
                  {model.isReasoning && (
                    <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded-sm text-xs font-medium">
                      reasoning
                    </span>
                  )}
                  {model.supportsVision && (
                    <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-sm text-xs font-medium">
                      vision
                    </span>
                  )}
                  {model.supportsCodeInterpreter && (
                    <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded-sm text-xs font-medium">
                      code interpreter
                    </span>
                  )}
                  {model.supportsFunctionCalling === false && (
                    <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded-sm text-xs font-medium">
                      no tools
                    </span>
                  )}
                  {model.supportsStreaming === false && (
                    <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded-sm text-xs font-medium">
                      no streaming
                    </span>
                  )}
                  {/* Legacy capabilities tags */}
                  {model.capabilities
                    ?.filter(
                      (c) =>
                        ![
                          "vision",
                          "chat",
                          "embedding",
                          "image",
                          "audio",
                          "research",
                          "function_calling",
                          "streaming",
                          "reasoning",
                          "code_interpreter",
                          "code interpreter",
                        ].includes(c),
                    )
                    .map((cap) => (
                      <span
                        key={cap}
                        className="px-2 py-0.5 bg-surface-secondary text-content-tertiary rounded-sm text-xs"
                      >
                        {cap}
                      </span>
                    ))}
                </div>

                <div className="mt-4 flex items-center gap-2">
                  <button
                    onClick={() => handleConfigure(model)}
                    className="flex-1 px-3 py-2 bg-surface-secondary hover:bg-interactive-active border border-border-primary rounded-lg text-sm text-content-primary transition-colors font-medium"
                  >
                    Configure
                  </button>
                  {assignableTiers.length > 0 && (
                    <div className="relative flex-1">
                      <select
                        aria-label={`Set ${model.name} as default`}
                        value=""
                        onChange={(e) => handleSetAsDefault(model, e.target.value)}
                        disabled={isSettingDefault}
                        className="w-full appearance-none px-3 py-2 bg-surface-secondary hover:bg-interactive-active border border-border-primary rounded-lg text-sm text-content-primary font-medium transition-colors disabled:opacity-50"
                      >
                        <option value="">Set as default…</option>
                        {assignableTiers.map((tier) => (
                          <option key={tier} value={tier}>
                            {tierLabel(tier)}
                          </option>
                        ))}
                      </select>
                      {isSettingDefault && (
                        <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary animate-spin" />
                      )}
                    </div>
                  )}
                  <button
                    onClick={() => handleDeleteModel(model)}
                    disabled={deletingId === model.id}
                    className="px-3 py-2 bg-surface-secondary hover:bg-red-500/10 border border-border-primary hover:border-red-500/30 rounded-lg text-sm text-content-tertiary hover:text-red-400 transition-colors disabled:opacity-50"
                    title="Delete model"
                  >
                    {deletingId === model.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {models.length === 0 && (
          <div className="text-center py-12">
            <Cpu className="w-12 h-12 text-content-quaternary mx-auto mb-3" />
            <p className="text-content-tertiary mb-4">No models configured yet</p>
            <button
              onClick={handleAddModel}
              className="px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              Add Your First Model
            </button>
          </div>
        )}
      </div>

      {showConfigModal && selectedModel && (
        <ConfigureModelModal
          model={selectedModel}
          models={models}
          onClose={() => {
            setShowConfigModal(false);
            setSelectedModel(null);
          }}
          onSave={async () => {
            await loadModels();
            await refreshModels();
            setShowConfigModal(false);
            setSelectedModel(null);
          }}
          confirmDisable={() => confirmReassign(selectedModel, "disable")}
        />
      )}

      {showAddModal && (
        <AddModelModal
          onClose={() => setShowAddModal(false)}
          onSave={async () => {
            await loadModels();
            await refreshModels();
            setShowAddModal(false);
          }}
        />
      )}

      {reassignDialog && (
        <ReassignModelModal
          model={reassignDialog.model}
          models={models}
          action={reassignDialog.action}
          references={reassignDialog.references}
          onClose={() => settleReassign(false)}
          onDone={() => settleReassign(true)}
        />
      )}
    </div>
  );
}
